import { v5 as uuidv5 } from 'uuid'
const WEBRTC_NAMESPACE = '0d02dd40-d435-4446-a350-8a32929a505b'
angular.module('webremote.service.webRtc', []).factory('webRtcService', function ($injector, toastr) {
  class WebRtc {
    constructor () {
      this.peerConnection = null
      this.systemConfig = {}
      this.localStream = null
      this.remoteStream = null
      this.tappedStream = ''
      this.iceCandidates = []
      this.iceCandidatesTimer = null
      this.offerSdp = {}
      this.webAudio = {}
      this.webAudioUuid = ''
      this.webRtcGroup = ''
      this.connectionState = ''
      this.elementName = ''

      // Audio analysis for local stream
      this.localAudioContext = null
      this.localAnalyser = null
      this.localAudioLevels = []
      this.localMute = false
      this.localChannelCount = 0
      this.localPeakData = {}

      // Audio analysis for remote stream
      this.remoteAudioContext = null
      this.remoteAnalyser = null
      this.remoteAudioLevels = []
      this.remoteMute = false
      this.remoteChannelCount = 0
      this.remotePeakData = {}

      this.sampleRate = 0
      this.isAnalyzing = false
      this.animationId = null
      this.updateCallback = null
    }

    async startConnection (elementName, audioMode, videoMode, updateCallback) {
      this.elementName = elementName
      if (updateCallback) {
        this.updateCallback = updateCallback
      }
      if (this.remoteStream) {
        document.getElementById(this.elementName).srcObject = this.remoteStream
        return
      }
      let serverConfiguration = {}
      const om = $injector.get('objectManagerService').getObjectManager()
      await om.collection('systemConfig').then((v) => {
        this.systemConfig = Array.from(v.values())[0]
      })
      if (this.systemConfig.member.audio.listenInMode.webRtc.stunServer) {
        serverConfiguration = { iceServers: [{ urls: 'stun:' + this.systemConfig.member.audio.listenInMode.webRtc.stunServer }] }
      }
      this.peerConnection = new RTCPeerConnection(serverConfiguration)
      this.peerConnection.addEventListener('icecandidate', this.handleConnection.bind(this))
      this.peerConnection.addEventListener('iceconnectionstatechange', this.handleConnectionChange.bind(this))
      this.peerConnection.addEventListener('track', async (event) => {
        this.remoteStream = event.streams[0]

        const audioEl = document.getElementById(this.elementName)
        if (audioEl.srcObject !== this.remoteStream) {
          audioEl.srcObject = this.remoteStream
          audioEl.muted = this.remoteMute
          audioEl.autoplay = true

          audioEl.play().catch(() => {
            const resume = () => { audioEl.play().finally(() => window.removeEventListener('click', resume)) }
            window.addEventListener('click', resume, { once: true })
          })
        }

        if (event.track.kind === 'audio') {
          this.setupRemoteAudioAnalysis()
        }
      })

      // We need at least one of audio and video to be true. We prefer audio.
      let aM = (audioMode === MediaMode.BIDIRECTIONAL)
      if (videoMode !== MediaMode.BIDIRECTIONAL) {
        aM = true
      }

      await navigator.mediaDevices.getUserMedia({ audio: aM, video: videoMode === MediaMode.BIDIRECTIONAL }).then((mediastream) => {
        this.localStream = mediastream
        this.setupLocalAudioAnalysis()
      }).catch((error) => {
        if (audioMode !== MediaMode.RECEIVE_ONLY) {
          console.log('navigator.mediaDevices.getUserMedia error: ', error)
        }
      })

      if (this.localStream) {
        this.localStream.getTracks().forEach(t => {
          if (t.kind === 'audio') {
            t.enabled = !this.localMute
          }
          this.peerConnection.addTrack(t, this.localStream)
        })
      }
      const createOfferParameters = {}
      if (audioMode === MediaMode.RECEIVE_ONLY) {
        createOfferParameters.offerToReceiveAudio = true
      }
      if (videoMode === MediaMode.RECEIVE_ONLY) {
        createOfferParameters.offerToReceiveVideo = true
      }

      this.peerConnection.createOffer(createOfferParameters).then((description) => {
        this.peerConnection.setLocalDescription(description).then(() => {
          this.offerSdp = description
        }).catch((error) => {
          console.log('localPeerConnection.setLocalDescription error: ', error)
        })
      }).catch((error) => {
        console.log('localPeerConnection.createOffer error: ', error)
      })
      this.connectionState = 'connecting'
      this.startAudioAnalysis(updateCallback)
    }

    startIceSendTimer (duration, action) {
      if (this.iceCandidatesTimer) {
        clearTimeout(this.iceCandidatesTimer)
      }

      this.iceCandidatesTimer = setTimeout(action, duration)
    }

    handleConnection (event) {
      if (event.candidate) {
        if (event.candidate.candidate !== '') {
          this.iceCandidates.push(JSON.stringify(event.candidate.toJSON()))
          this.startIceSendTimer(300, this.handleIceGatheringChange.bind(this))
        }
      }
    }

    handleConnectionChange (event) {
      const peerConnection = event.target
      this.connectionState = peerConnection.iceConnectionState
      if (peerConnection.iceConnectionState === 'disconnected') {
        this.stop()
      }
      if (peerConnection.iceConnectionState === 'failed') {
        toastr.error('WebRTC ICE failed. Please try to set another STUN server.', 'Error', {
          closeButton: true, progressBar: true, positionClass: 'toast-bottom-right'
        })
        this.stop()
      }
    }

    handleIceGatheringChange () {
      const om = $injector.get('objectManagerService').getObjectManager()

      om.getDefault('webAudio').then(async function (defaultObj) {
        const webAudio = {}
        webAudio.member = angular.copy(defaultObj)
        webAudio.member = {
          protocol: {
            webRtc: {
              offerSdp: JSON.stringify(this.offerSdp),
              answerSdp: '',
              offerIce: this.iceCandidates,
              answerIce: []
            }
          }
        }
        if (this.webRtcGroup === '') {
          webAudio.member.method = {
            tap: { tappedStream: this.tappedStream }
          }
        } else {
          webAudio.member.method = {
            webRtcGroup: { streamGroup: this.webRtcGroup }
          }
        }

        // ToDo: Check if object exists and delete it
        webAudio.member.uuid = uuidv5(this.elementName, WEBRTC_NAMESPACE)
        await om.object(webAudio.member.uuid, 'webAudio', true).then(function (webAudio) {
          if (webAudio !== undefined && typeof webAudio.member !== 'undefined') {
            webAudio.destroy()
          }
        })

        om.create('webAudio', webAudio.member).then(function (uuid) {
          this.webAudioUuid = uuid
          om.object(uuid, 'webAudio', true).then(function (webAudio) {
            this.webAudio = webAudio
          }.bind(this))
          setTimeout(this.waitForAnswerSdp.bind(this), 50)
        }.bind(this))
      }.bind(this))
    }

    waitForAnswerSdp (startTime = Date.now(), timeoutMs = 5000) {
      if (typeof this.webAudio !== 'undefined' && typeof this.webAudio.member !== 'undefined') {
        const om = $injector.get('objectManagerService').getObjectManager()
        om.object(this.webAudio.member.uuid, 'webAudio', true).then(function (webAudio) {
          this.webAudio = webAudio
        }.bind(this))
      }
      if (this.webAudio !== undefined && (typeof this.webAudio.member === 'undefined' || !this.webAudio.member.protocol.webRtc.answerSdp || this.webAudio.member.protocol.webRtc.answerIce.length === 0)) {
        const elapsed = Date.now() - startTime
        if (elapsed >= timeoutMs) {
          console.warn(`waitForAnswerSdp: Timed out after ${elapsed}ms without receiving answerSdp`)
          if (typeof this.webAudio.member !== 'undefined') {
            this.webAudio.destroy()
            this.webAudio = {}
          }
          this.webAudioUuid = ''
          return
        }
        setTimeout(() => this.waitForAnswerSdp(startTime, timeoutMs), 500)
      } else {
        this.peerConnection.setRemoteDescription(JSON.parse(this.webAudio.member.protocol.webRtc.answerSdp))
        for (const iceCandidate of this.webAudio.member.protocol.webRtc.answerIce) {
          this.peerConnection.addIceCandidate(JSON.parse(iceCandidate))
            .then(() => {
            }).catch((error) => {
              console.log('addIceCandidate failed with error:', error)
            })
        }
      }
    }

    setupLocalAudioAnalysis () {
      if (!this.localStream) return

      try {
        this.localAudioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.sampleRate = this.localAudioContext.sampleRate

        const source = this.localAudioContext.createMediaStreamSource(this.localStream)
        this.localAnalyser = this.localAudioContext.createAnalyser()

        // Analyser settings
        this.localAnalyser.fftSize = 2048
        this.localAnalyser.smoothingTimeConstant = 0.3

        source.connect(this.localAnalyser)

        // Determine channel count for local stream - use robust method
        this.localChannelCount = this.getStreamChannelCount(this.localStream, source)

        this.localAudioLevels = new Array(this.localChannelCount).fill(0).map(() => ({
          rms: 0,
          dB: -80,
          percentage: 0
        }))
      } catch (error) {
        console.error('Error setting up local audio analysis:', error)
      }
    }

    setupRemoteAudioAnalysis () {
      if (!this.remoteStream) return

      try {
        this.remoteAudioContext = new (window.AudioContext || window.webkitAudioContext)()

        const source = this.remoteAudioContext.createMediaStreamSource(this.remoteStream)
        this.remoteAnalyser = this.remoteAudioContext.createAnalyser()

        // Analyser settings
        this.remoteAnalyser.fftSize = 2048
        this.remoteAnalyser.smoothingTimeConstant = 0.3

        source.connect(this.remoteAnalyser)

        // Determine channel count for remote stream - more robust approach
        this.remoteChannelCount = this.getStreamChannelCount(this.remoteStream, source)

        this.remoteAudioLevels = new Array(this.remoteChannelCount).fill(0).map(() => ({
          rms: 0,
          dB: -80,
          percentage: 0
        }))
      } catch (error) {
        console.error('Error setting up remote audio analysis:', error)
      }
    }

    getStreamChannelCount (stream, audioSource = null) {
      const channelCount = 1 // Default fallback

      try {
        // Method 1: Try getSettings()
        const audioTrack = stream.getAudioTracks()[0]
        if (audioTrack) {
          const settings = audioTrack.getSettings()
          if (settings.channelCount && settings.channelCount > 0) {
            return settings.channelCount
          }

          // Method 2: Try getConstraints()
          const constraints = audioTrack.getConstraints()
          if (constraints.channelCount) {
            return constraints.channelCount
          }

          // Method 3: Try getCapabilities()
          const capabilities = audioTrack.getCapabilities()
          if (capabilities && capabilities.channelCount && capabilities.channelCount.max) {
            return capabilities.channelCount.max
          }
        }

        // Method 4: Try to determine from AudioContext source node
        if (audioSource && audioSource.channelCount) {
          return audioSource.channelCount
        }

        // Method 5: Check MediaStreamTrack label for hints
        if (audioTrack && audioTrack.label) {
          if (audioTrack.label.toLowerCase().includes('stereo')) {
            return 2
          } else if (audioTrack.label.toLowerCase().includes('mono')) {
            return 1
          }
        }
      } catch (error) {
        console.warn('Could not determine channel count, using default:', error)
      }

      return channelCount
    }

    startAudioAnalysis (updateCallback) {
      if (this.isAnalyzing) return

      this.isAnalyzing = true
      this.updateCallback = updateCallback

      const analyze = () => {
        if (!this.isAnalyzing) return

        // Analyze local stream
        if (this.localAnalyser && this.localChannelCount > 0) {
          this.analyzeAudioStream(this.localAnalyser, this.localChannelCount, this.localAudioLevels, this.localPeakData)
        }

        // Analyze remote stream
        if (this.remoteAnalyser && this.remoteChannelCount > 0) {
          this.analyzeAudioStream(this.remoteAnalyser, this.remoteChannelCount, this.remoteAudioLevels, this.remotePeakData)
        }

        // Call external update callback if provided
        if (this.updateCallback) {
          this.updateCallback({
            local: this.localAudioLevels,
            remote: this.remoteAudioLevels
          })
        }

        this.animationId = requestAnimationFrame(analyze)
      }

      analyze()
    }

    analyzeAudioStream (analyser, channelCount, audioLevels, peakData = {}) {
      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Float32Array(bufferLength)

      analyser.getFloatTimeDomainData(dataArray)

      // Initialize peak arrays if not exists
      if (!peakData.values) peakData.values = []
      if (!peakData.timestamps) peakData.timestamps = []

      // Calculate RMS for each channel
      const samplesPerChannel = Math.floor(bufferLength / channelCount)
      const currentTime = Date.now()

      // Peak-Decay Konstanten
      const PEAK_HOLD_TIME = 300 // 300ms hold time
      const PEAK_DECAY_RATE = 5 // 10dB/s fall-off rate

      for (let channel = 0; channel < channelCount; channel++) {
        let sum = 0
        const startIndex = channel * samplesPerChannel
        const endIndex = Math.min(startIndex + samplesPerChannel, bufferLength)

        for (let i = startIndex; i < endIndex; i++) {
          sum += dataArray[i] * dataArray[i]
        }

        const rms = Math.sqrt(sum / (endIndex - startIndex))

        // Convert RMS to dB
        const dB = rms > 0 ? 20 * Math.log10(rms) : -80
        const clampedDb = Math.max(-80, Math.min(0, dB))

        // Percentage for progress bar (0dB = 100%, -80dB = 0%)
        const percentage = ((clampedDb + 80) / 80) * 100

        // Peak-Decay Berechnung
        // Initialize peak values for this channel if not exists
        if (peakData.values[channel] === undefined) {
          peakData.values[channel] = clampedDb
          peakData.timestamps[channel] = currentTime
        }

        let peakDb = peakData.values[channel]
        const timeSinceLastPeak = currentTime - peakData.timestamps[channel]

        // Check if current value is higher than stored peak
        if (clampedDb > peakDb) {
          // New peak detected
          peakDb = clampedDb
          peakData.values[channel] = peakDb
          peakData.timestamps[channel] = currentTime
        } else {
          // Apply peak decay logic
          if (timeSinceLastPeak > PEAK_HOLD_TIME) {
            // Calculate decay: 10dB/s = 10dB/1000ms = 0.01dB/ms
            const decayTime = timeSinceLastPeak - PEAK_HOLD_TIME
            const decayAmount = (PEAK_DECAY_RATE / 1000) * decayTime
            peakDb = Math.max(clampedDb, peakData.values[channel] - decayAmount)

            // Update stored peak if it has decayed
            if (peakDb < peakData.values[channel]) {
              peakData.values[channel] = peakDb
            }
          }
          // If we're still in hold time, keep the stored peak value
        }

        // Calculate peak percentage
        const peakPercentage = ((peakDb + 80) / 80) * 100

        audioLevels[channel] = {
          rms: rms,
          dB: clampedDb.toFixed(1),
          percentage: Math.max(0, percentage),
          peakDb: peakDb.toFixed(1),
          peakPercentage: Math.max(0, peakPercentage),
          peakHolding: timeSinceLastPeak <= PEAK_HOLD_TIME
        }
      }
    }

    stopAudioAnalysis () {
      this.isAnalyzing = false
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }

      // Close local audio context
      if (this.localAudioContext && this.localAudioContext.state !== 'closed') {
        this.localAudioContext.close()
      }

      // Close remote audio context
      if (this.remoteAudioContext && this.remoteAudioContext.state !== 'closed') {
        this.remoteAudioContext.close()
      }
    }

    stop () {
      this.connectionState = ''
      this.stopAudioAnalysis()
      if (typeof this.peerConnection !== 'undefined' && this.peerConnection !== null) {
        this.peerConnection.close()
      }
      this.peerConnection = null
      if (typeof this.localStream !== 'undefined' && this.localStream !== null) {
        this.localStream.getTracks().forEach(track => track.stop())
      }
      this.localStream = null
      this.remoteStream = null
      this.localAudioLevels = []
      this.remoteAudioLevels = []
      // Clear media elements
      const localMedia = document.getElementById(this.elementName)
      if (localMedia) localMedia.srcObject = null

      if (typeof this.webAudio !== 'undefined' && typeof this.webAudio.member !== 'undefined') {
        this.webAudio.destroy()
        this.webAudio = {}
      }
      this.webAudioUuid = ''
      this.elementName = ''
      this.iceCandidates = []
      this.offerSdp = {}
    }

    toogleMute (kind, media) {
      if (kind === 'local') {
        this.localMute = !this.localMute
        this.setMute(kind, media, this.localMute)
      } else if (kind === 'remote') {
        this.remoteMute = !this.remoteMute
        this.setMute(kind, media, this.remoteMute)
      }
    }

    setMute (kind, media, mute) {
      if (this.peerConnection) {
        if (kind === 'local') {
          this.peerConnection.getSenders().forEach(s => {
            if (s.track && s.track.kind === media) s.track.enabled = !mute
          })
        } else if (kind === 'remote') {
          const audioEl = document.getElementById(this.elementName)
          audioEl.muted = mute
        }
      }
    }

    getAudioLevels () {
      return {
        local: this.localAudioLevels,
        remote: this.remoteAudioLevels
      }
    }

    getLocalAudioLevels () {
      return this.localAudioLevels
    }

    getRemoteAudioLevels () {
      return this.remoteAudioLevels
    }

    getChannelCount () {
      return {
        local: this.localChannelCount,
        remote: this.remoteChannelCount
      }
    }

    getLocalChannelCount () {
      return this.localChannelCount
    }

    getRemoteChannelCount () {
      return this.remoteChannelCount
    }

    getSampleRate () {
      return this.sampleRate
    }
  }

  // Define the enum for media stream states
  const MediaMode = Object.freeze({
    OFF: 'OFF',
    RECEIVE_ONLY: 'RECEIVE_ONLY',
    BIDIRECTIONAL: 'BIDIRECTIONAL'
  })
  const instances = {}

  return {
    getInstance: function (name) {
      if (!instances[name]) {
        instances[name] = new WebRtc(name)
      }
      return instances[name]
    },
    MediaMode: MediaMode
  }
})
